import { IVzdEntry } from '@alberta/konexi-shared';
import { Component } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { IKimRecipient, IKimRecipientFilter } from 'src/app/shared/models/kim/kim.model';
import { KimApiService, VzdUnavailableError } from 'src/app/shared/services/kim/kim-api.service';

import { KimRecipientFilterComponent } from './kim-recipient-filter/kim-recipient-filter.component';

export interface IKimRecipientSearchResult {
  recipient?: IKimRecipient;
  vzdUnavailable?: boolean;
}

@Component({
  selector: 'itl-kim-recipient-search',
  templateUrl: './kim-recipient-search.modal.html',
  styleUrls: ['./kim-recipient-search.modal.scss'],
})
export class KimRecipientSearchModal {
  public searchTerm = '';
  public filter: IKimRecipientFilter = {};
  public recipients: IKimRecipient[] = [];
  public searching = false;
  public hasSearched = false;

  public get filterActive(): boolean {
    return Boolean(this.filter.street || this.filter.postalCode || this.filter.locality);
  }

  constructor(
    private _modalController: ModalController,
    private _kimApiService: KimApiService
  ) {}

  public async search(): Promise<void> {
    const term = this.searchTerm?.trim();
    if (!term && !this.filterActive) {
      return;
    }

    this.searching = true;
    try {
      const result = await this._kimApiService.searchVzd({
        q: term || undefined,
        street: this.filter.street,
        postalCode: this.filter.postalCode,
        locality: this.filter.locality,
      });
      this.recipients = this.toRecipients(result.entries);
      this.hasSearched = true;
    } catch (error) {
      if (error instanceof VzdUnavailableError) {
        await this._modalController.dismiss({ vzdUnavailable: true } as IKimRecipientSearchResult);
        return;
      }
      window.logger.error('KIM VZD search failed', error);
      this.recipients = [];
      this.hasSearched = true;
    } finally {
      this.searching = false;
    }
  }

  public async select(recipient: IKimRecipient): Promise<void> {
    await this._modalController.dismiss({ recipient } as IKimRecipientSearchResult);
  }

  public async cancel(): Promise<void> {
    await this._modalController.dismiss();
  }

  public async openFilter(): Promise<void> {
    const modal = await this._modalController.create({
      component: KimRecipientFilterComponent,
      componentProps: { filter: this.filter },
      backdropDismiss: false,
      cssClass: 'fullPageModal',
    });
    await modal.present();
    const result = await modal.onDidDismiss();
    if (result.data) {
      this.filter = result.data as IKimRecipientFilter;
      await this.search();
    }
  }

  public async resetFilter(): Promise<void> {
    this.filter = {};
    await this.search();
  }

  public formatAddress(recipient: IKimRecipient): string {
    const location = [recipient.postalCode, recipient.locality].filter(Boolean).join(' ');
    return [recipient.street, location].filter(Boolean).join(', ');
  }

  /**
   * Einträge ohne KIM-Adresse werden nicht angezeigt. Einträge mit
   * mehreren KIM-Adressen werden auf eine Zeile pro Adresse aufgefächert.
   */
  private toRecipients(entries: IVzdEntry[]): IKimRecipient[] {
    return (entries ?? [])
      .filter(entry => entry.kimAddresses?.length)
      .flatMap(entry =>
        entry.kimAddresses.map(kimAddress => ({
          displayName: this.toDisplayName(entry),
          telematikId: entry.telematikId,
          kimAddress,
          street: entry.street,
          postalCode: entry.postalCode,
          locality: entry.locality,
        }))
      );
  }

  private toDisplayName(entry: IVzdEntry): string {
    if (entry.displayName) {
      return entry.displayName;
    }
    const personName = [entry.title, entry.givenName, entry.surname].filter(Boolean).join(' ');
    return personName || entry.organization || entry.telematikId || entry.uid;
  }
}
